import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Plus, X, Info } from "lucide-react";
import { getReport, saveReport, listClients } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const emptyPartner = { nome: "", participacao: "" };

const emptyCompany = {
  data_inicio: "",
  razao_social: "",
  cnae: "",
  endereco: "",
  iptu: "",
  capital_social: "",
  ponto_referencia: "",
};

const emptyProgress = {
  regin_numero: "",
  regin_data: "",
  redesim_data: "",
  protocolo_web_numero: "",
  protocolo_web_data: "",
  observacoes: "",
};

const emptyLicenses = {
  cbmerj: "",
  visa: "",
  smma: "",
  alvara: "",
};

const formatDateTime = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Section = ({ title, children }) => (
  <section className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6">
    <h2 className="font-display text-lg font-semibold text-[#0F2942] mb-4">
      {title}
    </h2>
    {children}
  </section>
);

export default function ReportDetail({ clientIdProp, embedded = false }) {
  const params = useParams();
  const clientId = clientIdProp || params.clientId;
  const { canEdit, userName } = useAuth();

  const [client, setClient] = useState(null);
  const [companyData, setCompanyData] = useState(emptyCompany);
  const [partners, setPartners] = useState([]);
  const [progress, setProgress] = useState(emptyProgress);
  const [licenses, setLicenses] = useState(emptyLicenses);
  const [audit, setAudit] = useState({ by: null, at: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const disabled = !canEdit;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [clients, report] = await Promise.all([
          listClients(),
          getReport(clientId),
        ]);
        if (!mounted) return;
        setClient(clients.find((c) => c.id === clientId) || null);
        setCompanyData({ ...emptyCompany, ...(report.company_data || {}) });
        setPartners(report.partners || []);
        setProgress({ ...emptyProgress, ...(report.process_progress || {}) });
        setLicenses({ ...emptyLicenses, ...(report.licenses || {}) });
        setAudit({
          by: report.last_updated_by,
          at: report.last_updated_at,
        });
      } catch (e) {
        toast.error("Erro ao carregar ficha");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [clientId]);

  const addPartner = () => {
    if (partners.length >= 10) {
      toast.error("Máximo de 10 sócios");
      return;
    }
    setPartners([...partners, { ...emptyPartner }]);
  };

  const removePartner = (i) => {
    setPartners(partners.filter((_, idx) => idx !== i));
  };

  const updatePartner = (i, key, value) => {
    setPartners(
      partners.map((p, idx) => (idx === i ? { ...p, [key]: value } : p))
    );
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const saved = await saveReport(clientId, {
        company_data: companyData,
        partners,
        process_progress: progress,
        licenses,
        updated_by: userName,
      });
      setAudit({ by: saved.last_updated_by, at: saved.last_updated_at });
      toast.success("Ficha salva com sucesso");
    } catch (e) {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-slate-400 text-sm">
        Carregando ficha...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="report-detail-page">
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Link
              to="/clientes"
              data-testid="back-to-reports"
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0F2942] mb-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Link>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F172A]">
              Ficha de Relatório de Processos
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Cliente: <span className="font-medium text-[#0F2942]">{client?.name || "—"}</span>
            </p>
          </div>
          {canEdit ? (
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-report-button"
              className="bg-[#0F2942] hover:bg-[#173E63] text-white"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          ) : (
            <div
              className="flex items-center gap-2 text-xs text-slate-500 bg-slate-100 px-3 py-2 rounded-lg"
              data-testid="viewer-mode-banner"
            >
              <Info className="h-4 w-4" />
              Modo Visualizador — campos bloqueados
            </div>
          )}
        </div>
      )}

      {/* Dados da Empresa */}
      <Section title="Dados da Empresa">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="data_inicio">Data de início</Label>
            <Input
              id="data_inicio"
              type="date"
              value={companyData.data_inicio}
              disabled={disabled}
              onChange={(e) =>
                setCompanyData({ ...companyData, data_inicio: e.target.value })
              }
              data-testid="field-data-inicio"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="razao_social">Razão Social</Label>
            <Input
              id="razao_social"
              value={companyData.razao_social}
              disabled={disabled}
              onChange={(e) =>
                setCompanyData({ ...companyData, razao_social: e.target.value })
              }
              data-testid="field-razao-social"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="cnae">CNAE</Label>
            <Input
              id="cnae"
              value={companyData.cnae}
              disabled={disabled}
              onChange={(e) =>
                setCompanyData({ ...companyData, cnae: e.target.value })
              }
              data-testid="field-cnae"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="iptu">N° IPTU</Label>
            <Input
              id="iptu"
              value={companyData.iptu}
              disabled={disabled}
              onChange={(e) =>
                setCompanyData({ ...companyData, iptu: e.target.value })
              }
              data-testid="field-iptu"
              className="mt-1"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="endereco">Endereço</Label>
            <Input
              id="endereco"
              value={companyData.endereco}
              disabled={disabled}
              onChange={(e) =>
                setCompanyData({ ...companyData, endereco: e.target.value })
              }
              data-testid="field-endereco"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="capital_social">Capital Social</Label>
            <Input
              id="capital_social"
              value={companyData.capital_social}
              disabled={disabled}
              onChange={(e) =>
                setCompanyData({ ...companyData, capital_social: e.target.value })
              }
              placeholder="R$ 0,00"
              data-testid="field-capital-social"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Ponto de referência?</Label>
            <Select
              value={companyData.ponto_referencia}
              disabled={disabled}
              onValueChange={(v) =>
                setCompanyData({ ...companyData, ponto_referencia: v })
              }
            >
              <SelectTrigger className="mt-1" data-testid="field-ponto-referencia">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sim">Sim</SelectItem>
                <SelectItem value="nao">Não</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      {/* Composição Societária */}
      <Section title="Composição Societária (até 10 sócios)">
        <div className="space-y-3">
          {partners.length === 0 && (
            <p className="text-sm text-slate-400 italic">Nenhum sócio cadastrado.</p>
          )}
          {partners.map((p, i) => (
            <div
              key={i}
              className="grid grid-cols-1 sm:grid-cols-[1fr_160px_auto] gap-3 items-end"
              data-testid={`partner-row-${i}`}
            >
              <div>
                <Label>Nome do sócio {i + 1}</Label>
                <Input
                  value={p.nome}
                  disabled={disabled}
                  onChange={(e) => updatePartner(i, "nome", e.target.value)}
                  data-testid={`partner-name-${i}`}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>% de participação</Label>
                <Input
                  value={p.participacao}
                  disabled={disabled}
                  onChange={(e) => updatePartner(i, "participacao", e.target.value)}
                  placeholder="Ex: 50%"
                  data-testid={`partner-percentage-${i}`}
                  className="mt-1"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                disabled={disabled}
                onClick={() => removePartner(i)}
                data-testid={`remove-partner-${i}`}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {!disabled && partners.length < 10 && (
            <Button
              variant="outline"
              onClick={addPartner}
              data-testid="add-partner-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Sócio
            </Button>
          )}
        </div>
      </Section>

      {/* Andamento do Processo */}
      <Section title="Andamento do Processo">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="regin_numero">N° REGIN</Label>
            <Input
              id="regin_numero"
              value={progress.regin_numero}
              disabled={disabled}
              onChange={(e) =>
                setProgress({ ...progress, regin_numero: e.target.value })
              }
              data-testid="field-regin-numero"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="regin_data">Data de conclusão REGIN</Label>
            <Input
              id="regin_data"
              type="date"
              value={progress.regin_data}
              disabled={disabled}
              onChange={(e) =>
                setProgress({ ...progress, regin_data: e.target.value })
              }
              data-testid="field-regin-data"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="redesim_data">Data de conclusão REDESIM</Label>
            <Input
              id="redesim_data"
              type="date"
              value={progress.redesim_data}
              disabled={disabled}
              onChange={(e) =>
                setProgress({ ...progress, redesim_data: e.target.value })
              }
              data-testid="field-redesim-data"
              className="mt-1"
            />
          </div>
          <div className="md:col-span-1" />
          <div>
            <Label htmlFor="protocolo_web_numero">N° PROTOCOLO WEB</Label>
            <Input
              id="protocolo_web_numero"
              value={progress.protocolo_web_numero}
              disabled={disabled}
              onChange={(e) =>
                setProgress({ ...progress, protocolo_web_numero: e.target.value })
              }
              data-testid="field-protocolo-web-numero"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="protocolo_web_data">Data de conclusão PROTOCOLO WEB</Label>
            <Input
              id="protocolo_web_data"
              type="date"
              value={progress.protocolo_web_data}
              disabled={disabled}
              onChange={(e) =>
                setProgress({ ...progress, protocolo_web_data: e.target.value })
              }
              data-testid="field-protocolo-web-data"
              className="mt-1"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea
              id="observacoes"
              value={progress.observacoes}
              disabled={disabled}
              onChange={(e) =>
                setProgress({ ...progress, observacoes: e.target.value })
              }
              rows={4}
              data-testid="field-observacoes"
              className="mt-1"
              placeholder="Anotações sobre o andamento..."
            />
          </div>
        </div>
      </Section>

      {/* Situação das Licenças */}
      <Section title="Situação das Licenças">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="cbmerj">Situação CBMERJ</Label>
            <Input
              id="cbmerj"
              value={licenses.cbmerj}
              disabled={disabled}
              onChange={(e) => setLicenses({ ...licenses, cbmerj: e.target.value })}
              data-testid="field-cbmerj"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="visa">Situação VISA</Label>
            <Input
              id="visa"
              value={licenses.visa}
              disabled={disabled}
              onChange={(e) => setLicenses({ ...licenses, visa: e.target.value })}
              data-testid="field-visa"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="smma">Situação SMMA</Label>
            <Input
              id="smma"
              value={licenses.smma}
              disabled={disabled}
              onChange={(e) => setLicenses({ ...licenses, smma: e.target.value })}
              data-testid="field-smma"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="alvara">ALVARÁ</Label>
            <Input
              id="alvara"
              value={licenses.alvara}
              disabled={disabled}
              onChange={(e) => setLicenses({ ...licenses, alvara: e.target.value })}
              data-testid="field-alvara"
              className="mt-1"
            />
          </div>
        </div>
      </Section>

      {/* Save button (bottom) */}
      {canEdit && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-report-button-bottom"
            className="bg-[#0F2942] hover:bg-[#173E63] text-white"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Salvando..." : "Salvar Ficha"}
          </Button>
        </div>
      )}

      {/* Auditoria */}
      {audit.by && audit.at && (
        <div
          className="text-xs text-slate-500 border-t border-slate-200 pt-4 pb-2"
          data-testid="audit-info"
        >
          Última atualização feita por:{" "}
          <span className="font-medium text-[#0F2942]">{audit.by}</span> em{" "}
          <span className="font-medium text-[#0F2942]">
            {formatDateTime(audit.at)}
          </span>
        </div>
      )}
    </div>
  );
}
